import {useState, useCallback, useRef, useMemo, useEffect, type CSSProperties, type ChangeEvent} from 'react'
import {Accordion, AccordionSummary, AccordionDetails, Typography, Box, Slider, Button} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LightModeIcon from '@mui/icons-material/LightMode'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  updateLight,
  setAmbientIntensity,
  setAmbientColor,
  setEnvPreset,
  setEnvMapIntensity,
  setGroundDistance,
  selectActiveScene,
} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import LightDirectionPad from '../sharedWidgets/LightDirectionPad'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import SliderRow from '../sharedWidgets/SliderRow'
import EnvPresetModal from './EnvPresetModal'
import {DREI_HDRI_CDN, DREI_PRESETS} from '../../../common/constants/envPresets'

// ── EnvMapPreview — tone-mapped preview of HDR ──────────────────

const EnvMapPreview = ({url}: {url: string}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!url) return
    let disposed = false

    const loadAndRender = async () => {
      const THREE = await import('three')
      const {HDRLoader} = await import('three/addons/loaders/HDRLoader.js')

      const loader = new HDRLoader()
      loader.setDataType(THREE.FloatType)

      loader.load(url, (texture) => {
        if (disposed) {
          texture.dispose()
          return
        }
        const canvas = canvasRef.current
        if (!canvas) {
          texture.dispose()
          return
        }

        const img = texture.image
        const aspect = img.width / img.height
        const cw = 300
        const ch = Math.round(cw / aspect)
        canvas.width = cw
        canvas.height = ch
        const ctx = canvas.getContext('2d')!

        const data = img.data as Float32Array
        const channels = data.length / (img.width * img.height)
        const imageData = ctx.createImageData(cw, ch)
        const pixels = imageData.data

        for (let y = 0; y < ch; y++) {
          for (let x = 0; x < cw; x++) {
            const sx = Math.floor((x / cw) * img.width)
            const sy = Math.floor((y / ch) * img.height)
            const srcIdx = (sy * img.width + sx) * channels

            let r = data[srcIdx] || 0
            let g = data[srcIdx + 1] || 0
            let b = data[srcIdx + 2] || 0

            // Reinhard tone map + gamma correction
            r = Math.pow(r / (1 + r), 1 / 2.2)
            g = Math.pow(g / (1 + g), 1 / 2.2)
            b = Math.pow(b / (1 + b), 1 / 2.2)

            const dstIdx = (y * cw + x) * 4
            pixels[dstIdx] = Math.min(255, r * 255)
            pixels[dstIdx + 1] = Math.min(255, g * 255)
            pixels[dstIdx + 2] = Math.min(255, b * 255)
            pixels[dstIdx + 3] = 255
          }
        }

        ctx.putImageData(imageData, 0, 0)
        texture.dispose()
      })
    }

    loadAndRender()
    return () => {
      disposed = true
    }
  }, [url])

  return <canvas ref={canvasRef} style={{width: '100%', height: 'auto', display: 'block', borderRadius: 4}} />
}

// ── LightingShadowPanel — lighting & shadows sub-accordion ────────

const LightingShadowPanel = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()

  const lights = useAppSelector((s) => selectActiveScene(s).lights)
  const ambientIntensity = useAppSelector((s) => selectActiveScene(s).ambientIntensity)
  const ambientColor = useAppSelector((s) => selectActiveScene(s).ambientColor)
  const envPreset = useAppSelector((s) => selectActiveScene(s).envPreset)
  const envMapIntensity = useAppSelector((s) => selectActiveScene(s).envMapIntensity)
  const groundDistance = useAppSelector((s) => selectActiveScene(s).groundDistance)

  const [envModalOpen, setEnvModalOpen] = useState(false)

  // ── Light handlers ────────────────────────────────────────────

  const handleLightColor = useCallback(
    (id: string, e: ChangeEvent<HTMLInputElement>) => {
      dispatch(updateLight({id, changes: {color: e.target.value}}))
    },
    [dispatch],
  )

  const handleLightIntensity = useCallback(
    (id: string, value: number) => {
      dispatch(updateLight({id, changes: {intensity: value}}))
    },
    [dispatch],
  )

  const handleLightPosXY = useCallback(
    (id: string, x: number, y: number) => {
      const light = lights.find((l) => l.id === id)
      if (!light) return
      dispatch(updateLight({id, changes: {position: [x, y, light.position[2]]}}))
    },
    [dispatch, lights],
  )

  const handleLightPosZ = useCallback(
    (id: string, value: number) => {
      const light = lights.find((l) => l.id === id)
      if (!light) return
      dispatch(updateLight({id, changes: {position: [light.position[0], light.position[1], value]}}))
    },
    [dispatch, lights],
  )

  const handleShadowIntensity = useCallback(
    (id: string, value: number) => {
      dispatch(updateLight({id, changes: {shadowIntensity: value}}))
    },
    [dispatch],
  )

  const handleShadowSoftness = useCallback(
    (id: string, value: number) => {
      dispatch(updateLight({id, changes: {shadowSoftness: value}}))
    },
    [dispatch],
  )

  // ── Ambient handlers ──────────────────────────────────────────

  const handleAmbientIntensity = useCallback(
    (value: number) => {
      dispatch(setAmbientIntensity(value))
    },
    [dispatch],
  )

  const handleAmbientColor = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(setAmbientColor(e.target.value))
    },
    [dispatch],
  )

  // ── Ground distance handler ───────────────────────────────────

  const handleGroundDistance = useCallback(
    (value: number) => {
      dispatch(setGroundDistance(value))
    },
    [dispatch],
  )

  const handleSealUndo = useCallback(() => {
    dispatch(sealUndo())
  }, [dispatch])

  // ── Env preset handlers ───────────────────────────────────────

  const handleEnvModalOpen = useCallback(() => {
    setEnvModalOpen(true)
  }, [])

  const handleEnvModalClose = useCallback(() => {
    setEnvModalOpen(false)
  }, [])

  const handleEnvPresetSelect = useCallback(
    (presetName: string) => {
      dispatch(setEnvPreset(presetName))
      dispatch(sealUndo())
      setEnvModalOpen(false)
    },
    [dispatch],
  )

  const handleEnvMapIntensity = useCallback(
    (value: number) => {
      dispatch(setEnvMapIntensity(value))
    },
    [dispatch],
  )

  const previewUrl = DREI_PRESETS[envPreset] ? DREI_HDRI_CDN + DREI_PRESETS[envPreset] : ''

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  return (
    <Accordion disableGutters square sx={sx.subAccordion}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.subSummary}>
        <LightModeIcon sx={sx.subIcon} />
        <Typography sx={sx.subTitle}>{t('scene.lighting.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={sx.subDetails}>
        {/* ── Light sections ──────────────────────────────────── */}
        {lights.map((light) => (
          <Box key={light.id} sx={sx.groupCard}>
            <Accordion disableGutters square sx={sx.sectionAccordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.sectionExpandIcon} />} sx={sx.sectionSummary}>
                <Typography sx={sx.lightLabel}>{light.label}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={sx.sectionDetails}>
                {/* Color */}
                <Box sx={sx.row}>
                  <Typography sx={sx.fieldLabel}>{t('scene.lighting.color')}</Typography>
                  <input
                    type="color"
                    value={light.color}
                    onChange={(e) => handleLightColor(light.id, e)}
                    onBlur={handleSealUndo}
                    style={sx.colorInput}
                  />
                </Box>

                {/* Intensity (1-100%) */}
                <SliderRow
                  label={t('scene.lighting.intensity')}
                  value={light.intensity}
                  min={1}
                  max={100}
                  step={1}
                  unit="%"
                  onChange={(v: number) => handleLightIntensity(light.id, v)}
                  onChangeCommitted={handleSealUndo}
                />

                {/* Position: pad (X/Y) + vertical slider (Z) */}
                <Box sx={sx.padSection}>
                  <Typography sx={sx.fieldLabel}>{t('scene.lighting.direction')}</Typography>
                  <Box sx={sx.padWithSlider}>
                    <LightDirectionPad
                      valueX={light.position[0]}
                      valueY={light.position[1]}
                      onChange={(x, y) => handleLightPosXY(light.id, x, y)}
                      onDragEnd={handleSealUndo}
                    />
                    <Box sx={sx.verticalSliderBox}>
                      <Typography sx={sx.verticalSliderLabel}>Z</Typography>
                      <Slider
                        value={light.position[2]}
                        min={-100}
                        max={100}
                        step={5}
                        orientation="vertical"
                        onChange={(_, v) => handleLightPosZ(light.id, v as number)}
                        onChangeCommitted={handleSealUndo}
                        sx={sx.verticalSlider}
                        size="small"
                      />
                      <Typography sx={sx.verticalSliderValue}>{light.position[2]}</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Shadow intensity (0-100%) — only for directional lights */}
                {light.type !== 'rectarea' && (
                  <SliderRow
                    label={t('scene.lighting.shadowIntensity')}
                    value={light.shadowIntensity}
                    min={0}
                    max={100}
                    step={1}
                    unit="%"
                    onChange={(v: number) => handleShadowIntensity(light.id, v)}
                    onChangeCommitted={handleSealUndo}
                  />
                )}

                {/* Shadow softness (0-100%) — only for directional lights */}
                {light.type !== 'rectarea' && (
                  <SliderRow
                    label={t('scene.lighting.shadowSoftness')}
                    value={light.shadowSoftness}
                    min={0}
                    max={100}
                    step={1}
                    unit="%"
                    onChange={(v: number) => handleShadowSoftness(light.id, v)}
                    onChangeCommitted={handleSealUndo}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        ))}

        {/* ── Ambient + Ground section ────────────────────────── */}
        <Box sx={sx.groupCard}>
          <Accordion disableGutters square sx={sx.sectionAccordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.sectionExpandIcon} />} sx={sx.sectionSummary}>
              <Typography sx={sx.lightLabel}>{t('scene.lighting.ambient')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={sx.sectionDetails}>
              <Box sx={sx.row}>
                <Typography sx={sx.fieldLabel}>{t('scene.lighting.ambientColor')}</Typography>
                <input
                  type="color"
                  value={ambientColor}
                  onChange={handleAmbientColor}
                  onBlur={handleSealUndo}
                  style={sx.colorInput}
                />
              </Box>

              <SliderRow
                label={t('scene.lighting.ambientIntensity')}
                value={ambientIntensity}
                min={1}
                max={100}
                step={1}
                unit="%"
                onChange={handleAmbientIntensity}
                onChangeCommitted={handleSealUndo}
              />

              <SliderRow
                label={t('scene.lighting.groundDistance')}
                value={groundDistance}
                min={0}
                max={100}
                step={1}
                unit=""
                onChange={handleGroundDistance}
                onChangeCommitted={handleSealUndo}
              />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* ── Environment map section ─────────────────────────── */}
        <Box sx={sx.groupCard}>
          <Accordion disableGutters square sx={sx.sectionAccordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.sectionExpandIcon} />} sx={sx.sectionSummary}>
              <Typography sx={sx.lightLabel}>{t('scene.lighting.envMap')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={sx.sectionDetails}>
              {/* Preview */}
              <Box sx={sx.previewBox}>
                {previewUrl ? (
                  <EnvMapPreview url={previewUrl} />
                ) : (
                  <Typography sx={sx.previewLabel}>{envPreset}</Typography>
                )}
              </Box>

              <Button variant="outlined" size="small" onClick={handleEnvModalOpen} sx={sx.changeButton}>
                {t('scene.lighting.envMapChange')}
              </Button>

              <SliderRow
                label={t('scene.lighting.envMapIntensity')}
                value={envMapIntensity}
                min={0}
                max={100}
                step={1}
                unit="%"
                onChange={handleEnvMapIntensity}
                onChangeCommitted={handleSealUndo}
              />
            </AccordionDetails>
          </Accordion>
        </Box>

        <EnvPresetModal
          open={envModalOpen}
          onClose={handleEnvModalClose}
          onSelect={handleEnvPresetSelect}
          currentPreset={envPreset}
        />
      </AccordionDetails>
    </Accordion>
  )
}

// ── Styles ────────────────────────────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  subAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  subSummary: {
    minHeight: 40,
    padding: '0 4px',
    '&.Mui-expanded': {minHeight: 40},
    '& .MuiAccordionSummary-content': {
      margin: '8px 0',
      alignItems: 'center',
      gap: '6px',
      '&.Mui-expanded': {margin: '8px 0'},
    },
  },
  expandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 18,
  },
  subIcon: {
    color: tokens.accordion.sectionIcon,
    fontSize: 16,
  },
  subTitle: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: tokens.text.secondary,
  },
  subDetails: {
    padding: '4px 4px 12px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: tokens.text.muted,
  },
  groupCard: {
    borderRadius: '6px',
    border: `1px solid ${tokens.accordion.border}`,
    overflow: 'hidden',
  },
  sectionAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  sectionSummary: {
    minHeight: 32,
    padding: '0 8px',
    '&.Mui-expanded': {minHeight: 32},
    '& .MuiAccordionSummary-content': {
      margin: '4px 0',
      alignItems: 'center',
      '&.Mui-expanded': {margin: '4px 0'},
    },
  },
  sectionExpandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 16,
  },
  sectionDetails: {
    padding: '0 8px 8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  lightLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: tokens.text.primary,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  fieldLabel: {
    fontSize: 11,
    color: tokens.text.muted,
    fontWeight: 500,
    minWidth: 60,
    flexShrink: 0,
  },
  colorInput: {
    width: 32,
    height: 24,
    border: `1px solid ${tokens.accordion.border}`,
    borderRadius: 4,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 1,
  } as CSSProperties,
  padSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  padWithSlider: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '8px',
  },
  verticalSliderBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '2px',
    minWidth: 24,
  },
  verticalSliderLabel: {
    fontSize: 9,
    fontWeight: 600,
    color: tokens.text.muted,
  },
  verticalSlider: {
    flex: 1,
    '& .MuiSlider-thumb': {width: 10, height: 10},
    '& .MuiSlider-rail': {opacity: 0.3},
  },
  verticalSliderValue: {
    fontSize: 9,
    fontWeight: 500,
    color: tokens.text.secondary,
  },
  previewBox: {
    borderRadius: '6px',
    border: `1px solid ${tokens.accordion.border}`,
    overflow: 'hidden',
  },
  previewLabel: {
    fontSize: 11,
    color: tokens.text.secondary,
    fontWeight: 500,
    textAlign: 'center' as const,
    padding: '16px',
    textTransform: 'capitalize' as const,
  },
  changeButton: {
    fontSize: 11,
    textTransform: 'none' as const,
    color: tokens.text.secondary,
    borderColor: tokens.accordion.border,
    '&:hover': {
      borderColor: tokens.text.muted,
    },
  },
})

export default LightingShadowPanel
